import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import {
  RAHMENSTATUSNAME,
  RESTMENGENNAME,
  abrufen,
  akte as holeAkte,
  anlegen,
  feldSichern,
  liste,
  meldungen as holeMeldungen,
  restmengeAbschliessen,
  type Rahmenauftrag,
  type Rahmenmeldung,
  type Rahmenstand,
  type Rahmenstatus,
  type Rahmenzeile,
  type Restmengenregel,
} from '../api/rahmenauftraege';
import { liste as artikelliste, type Artikelzeile } from '../api/produkte';
import type { Kundenzeile } from '../api/kunden';
import { ApiFehler } from '../api/fehler';
import { markenklasse, type Markenart } from '../marken';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { alsTag } from '../datum';
import { alsEuro, alsMenge, alsProzent } from '../betrag';
import type { Filtermarke } from '../komponenten/Filtermarken';
import { Liste } from '../komponenten/Liste';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { Erfassung } from '../komponenten/Erfassung';
import { Kundenfeld, Kundensuchfenster } from '../suche/Kundenwahl';
import { Platzterminwahl } from './Platzterminwahl';
import {
  Abschnitt,
  Aktenrumpf,
  Feldraster,
  Feldzeile,
  Kennzahlenleiste,
  Reiterleiste,
  Verlauf,
  type Kennzahl,
  type Verlaufseintrag,
} from '../komponenten/Akte';
import { STATUSNAME as AUFTRAGSSTATUSNAME } from '../api/auftraege';
import type { Kopf } from '../komponenten/Rahmen';
import { KundeVerweis } from '../komponenten/Kontaktkarte';

/** Der heutige Tag als ISO-Datum, ohne Zeitzonensprung. */
function heute(): string {
  const j = new Date();
  return `${j.getFullYear()}-${String(j.getMonth() + 1).padStart(2, '0')}-${String(j.getDate()).padStart(2, '0')}`;
}

function inMonaten(monate: number): string {
  const j = new Date();
  j.setMonth(j.getMonth() + monate);
  return `${j.getFullYear()}-${String(j.getMonth() + 1).padStart(2, '0')}-${String(j.getDate()).padStart(2, '0')}`;
}

const STATUSMARKE: Readonly<Record<Rahmenstatus, Markenart>> = {
  /* Ein laufender Rahmenauftrag ist der Normalfall. */
  aktiv: 'laufend',
  /* Das Kontingent ist weg — jetzt ist etwas zu verhandeln. */
  ausgeschoepft: 'aufmerksamkeit',
  abgelaufen: 'sackgasse',
  abgeschlossen: 'stillgelegt',
};

/**
 * Ein Balken für die Ausschöpfung.
 *
 * ALS TEXT UND ALS BALKEN. WCAG 1.4.1: Wer die Länge nicht abschätzt, liest
 * die Zahl — und wer die Liste überfliegt, sieht den Balken. Der Balken allein
 * wäre eine Auskunft, die von der Sehschärfe abhängt.
 */
function Ausschoepfung({ stand }: { readonly stand: Rahmenstand }): JSX.Element {
  const quote = Math.min(100, Number(stand.ausgeschoepftProzent));
  return (
    <>
      <span aria-hidden="true" className="ausschoepfung">
        {/* Die Breite ist der Messwert selbst — sie bleibt als einziger Wert am Element. */}
        <span
          className={quote >= 80 ? 'ausschoepfung__balken ausschoepfung__balken--knapp' : 'ausschoepfung__balken'}
          style={{ width: `${String(quote)}%` }}
        />
      </span>
      {`${alsMenge(stand.abgerufen)} von ${alsMenge(stand.abrufmenge)} (${alsProzent(stand.ausgeschoepftProzent)})`}
    </>
  );
}

/**
 * Die Rahmenauftragsliste (AUF-25, AUF-26).
 *
 * DIE RESTMENGE STEHT IN DER LISTE UND NICHT ERST IN DER AKTE. AUF-26 sagt
 * „jederzeit sichtbar" — und wer wissen will, welcher Rahmen zur Neige geht,
 * will es nicht durch dreißig Akten hindurch erfahren.
 *
 * Seitentyp Liste (ÄA-41): Zähler und „Neuer Rahmenauftrag" im Seitenkopf
 * (die Arbeitsfläche bietet die Anlage dort an); der Leerzustand behält
 * seinen Knopf.
 */
export function Rahmenauftragsliste({
  zurKunde,
  beiOeffnen,
  beiNeu,
}: {
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  readonly beiOeffnen: (id: string) => void;
  readonly beiNeu: () => void;
}): JSX.Element {
  const [suche, setzeSuche] = useState('');
  const [status, setzeStatus] = useState<Rahmenstatus | ''>('');
  const [rahmen, setzeRahmen] = useState<readonly Rahmenzeile[]>([]);
  const [meldungen, setzeMeldungen] = useState<readonly Rahmenmeldung[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (s: string, z: Rahmenstatus | ''): Promise<void> => {
    setzeLaedt(true);
    try {
      setzeRahmen(await liste({ suche: s, status: z }));
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    const kennung = setTimeout(() => void laden(suche, status), 250);
    return () => clearTimeout(kennung);
  }, [suche, status, laden]);

  /* Die aktiven Filter als abwaehlbare Marken (2.9). */
  const marken: readonly Filtermarke[] =
    status === ''
      ? []
      : [{ text: `Status: ${RAHMENSTATUSNAME[status]}`, entfernen: () => setzeStatus('') }];

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(rahmen.length, 'Rahmenauftrag', 'Rahmenaufträge'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        suche={{
          wert: suche,
          aendern: setzeSuche,
          beschriftung: 'Rahmenaufträge suchen',
          platzhalter: 'Rahmennummer oder Bezeichnung',
        }}
        filter={
          <>
            <select
              className="liste__filter"
              value={status}
              onChange={(e) => setzeStatus(e.target.value as Rahmenstatus | '')}
              aria-label="Status"
            >
              <option value="">Alle Status</option>
              {(Object.keys(RAHMENSTATUSNAME) as Rahmenstatus[]).map((s) => (
                <option key={s} value={s}>
                  {RAHMENSTATUSNAME[s]}
                </option>
              ))}
            </select>
            {/* AUF-26 */}
            <button
              type="button"
              className="knopf"
              onClick={() =>
                void holeMeldungen()
                  .then(setzeMeldungen)
                  .catch((u: unknown) => setzeFehler(u as ApiFehler))
              }
            >
              Fällige Meldungen
            </button>
          </>
        }
        marken={marken}
        hinweise={
          meldungen.length > 0 ? (
            <div className="karte">
              <div className="karte__titel">
                <h2>Fällige Meldungen</h2>
                {/* AUF-26 */}
              </div>
              <ul className="kanalliste">
                {meldungen.map((m) => (
                  <li key={`${m.rahmen}-${m.anlass}`}>
                    <button
                      type="button"
                      className="liste__oeffnen liste__nummer"
                      onClick={() => beiOeffnen(m.rahmen)}
                    >
                      {m.rahmennummer}
                    </button>{' '}
                    {m.anlass === 'ausschoepfung'
                      ? `— ${alsProzent(m.ausgeschoepftProzent)} abgerufen, ${alsMenge(m.restmenge)} offen`
                      : `— Laufzeit endet am ${alsTag(m.laufzeitBis) ?? '—'}, ${alsMenge(m.restmenge)} nicht abgerufen`}
                  </li>
                ))}
              </ul>
              <p className="feld__hilfe">
                Jede Meldung erscheint genau einmal — sonst wäre sie nach einer Woche Rauschen,
                und die achte, die neue, ginge mit unter. Ein Versandweg (E-Mail, Aufgabe) kommt
                später hinzu; bis dahin steht die Fälligkeit hier.
              </p>
            </div>
          ) : undefined
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">{rahmen.length} Rahmenaufträge</caption>
            <thead>
              <tr>
                <th scope="col" className="liste__spalte--knapp">
                  Nummer
                </th>
                <th scope="col">Kunde</th>
                <th scope="col">Bezeichnung</th>
                <th scope="col">Ausschöpfung</th>
                <th scope="col" className="zahl">
                  Rest
                </th>
                <th scope="col">Laufzeit bis</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rahmen.map((r) => (
                <tr key={r.id}>
                  <td className="liste__spalte--knapp">
                    <button type="button" className="liste__oeffnen liste__nummer" onClick={() => beiOeffnen(r.id)}>
                      {r.rahmennummer}
                    </button>
                  </td>
                  <td className="liste__name">
                  <KundeVerweis kundeId={r.kunde} zurAkte={zurKunde}>
                    {r.kundenname}
                  </KundeVerweis>
                </td>
                  <td>
                    {r.bezeichnung}
                    {r.artikelnummer !== null && (
                      <span className="kanalliste__name"> · {r.artikelnummer}</span>
                    )}
                  </td>
                  <td>
                    <Ausschoepfung stand={r.stand} />
                  </td>
                  <td className="zahl">{alsMenge(r.stand.restmenge)}</td>
                  <td>
                    <span className="liste__einzeilig">{alsTag(r.laufzeitBis)}</span>
                    {r.stand.tageBisAblauf >= 0 && r.stand.tageBisAblauf <= 60 && (
                      <span className="kanalliste__name">
                        {' '}
                        · noch {r.stand.tageBisAblauf} Tage
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={markenklasse(STATUSMARKE[r.status])}>
                      {RAHMENSTATUSNAME[r.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && rahmen.length === 0 && (
          <div className="liste__leer" role="status">
            <p>
              Noch kein Rahmenauftrag. Sie lohnen sich für Kunden mit wiederkehrendem Bedarf —
              Fahrschulen, Flottenbetreiber.
            </p>
            <button type="button" className="knopf knopf--haupt" onClick={beiNeu}>
              Rahmenauftrag anlegen
            </button>
          </div>
        )}
      </Liste>
    </>
  );
}

/**
 * Die Neuanlage eines Rahmenauftrags (AUF-25).
 *
 * DER GEBUNDENE PREIS DARF LEER BLEIBEN. Dann nimmt der Server den Preis, der
 * zum **Beginn der Laufzeit** gilt (PRF-05) — nicht den von heute. Ein Rahmen
 * ab Januar, im November erfasst, bände sonst den Novemberpreis.
 *
 * Seitentyp Erfassung (ÄA-41): eine Karte mit drei Abschnitten, „Anlegen" und
 * „Abbrechen" im Seitenkopf und am Ende — ein Formular, eine Prüfung.
 */
export function RahmenauftragAnlegen({
  beiAngelegt,
  beiAbbruch,
}: {
  readonly beiAngelegt: (id: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  // M-07: der Kunde über das Suchfenster, wie in den anderen Anlegen-Masken (Auftrag CRM-172).
  const [waehlt, setzeWaehlt] = useState(false);
  const [kunde, setzeKunde] = useState<Kundenzeile | undefined>(undefined);
  const [artikelsuche, setzeArtikelsuche] = useState('');
  const [artikeltreffer, setzeArtikeltreffer] = useState<readonly Artikelzeile[]>([]);
  const [artikel, setzeArtikel] = useState<Artikelzeile | undefined>(undefined);
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [abrufmenge, setzeAbrufmenge] = useState('100');
  const [preis, setzePreis] = useState('');
  const [von, setzeVon] = useState(heute());
  const [bis, setzeBis] = useState(inMonaten(12));
  const [regel, setzeRegel] = useState<Restmengenregel | ''>('');
  const [monate, setzeMonate] = useState('12');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  useEffect(() => {
    if (artikelsuche.trim().length < 3) {
      setzeArtikeltreffer([]);
      return;
    }
    let verworfen = false;
    const uhr = setTimeout(() => {
      void artikelliste({ suche: artikelsuche.trim(), nurWaehlbar: true })
        .then((e) => {
          if (!verworfen) setzeArtikeltreffer(e.artikel.slice(0, 8));
        })
        .catch(() => {
          if (!verworfen) setzeArtikeltreffer([]);
        });
    }, 250);
    return () => {
      verworfen = true;
      clearTimeout(uhr);
    };
  }, [artikelsuche]);

  async function absenden(): Promise<void> {
    if (kunde === undefined || artikel === undefined || bezeichnung === '') return;
    setzeLaeuft(true);
    try {
      const neu = await anlegen({
        kunde: kunde.id,
        bezeichnung,
        artikel: artikel.id,
        abrufmenge,
        laufzeitVon: von,
        laufzeitBis: bis,
        ...(preis === '' ? {} : { gebundenerPreis: preis }),
        ...(regel === '' ? {} : { restmengenregel: regel }),
        ...(regel === 'verlaengern' ? { verlaengerungMonate: Number(monate) } : {}),
      });
      beiAngelegt(neu.id);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  const vollstaendig = kunde !== undefined && artikel !== undefined && bezeichnung !== '';

  return (
    <div className="erfassung">
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {waehlt && (
        <Kundensuchfenster
          beiWahl={setzeKunde}
          beiFehler={setzeFehler}
          beiSchliessen={() => setzeWaehlt(false)}
        />
      )}

      {/* AUF-25. Das Suchfenster steht außerhalb der Karte. */}
      <Erfassung
        beschriftung="Neuer Rahmenauftrag"
        beiAbsenden={() => {
          if (vollstaendig && !laeuft) void absenden();
        }}
        absenden={{ text: laeuft ? 'Wird gespeichert …' : 'Anlegen', gesperrt: !vollstaendig || laeuft }}
        abbrechen={{ text: 'Abbrechen', beiKlick: beiAbbruch }}
      >
        <Abschnitt titel="Kunde und Leistung">
          <Feldraster>
            <Kundenfeld kennung="ra-kunde" kunde={kunde} offen={waehlt} beiSuchen={() => setzeWaehlt(true)} />

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ra-bezeichnung">
                Bezeichnung
              </label>
              <input
                id="ra-bezeichnung"
                type="text"
                value={bezeichnung}
                onChange={(e) => setzeBezeichnung(e.target.value)}
                placeholder="Fahrschule Meier 2027"
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ra-artikel">
                Artikel
              </label>
              {artikel === undefined ? (
                <input
                  id="ra-artikel"
                  type="text"
                  value={artikelsuche}
                  onChange={(e) => setzeArtikelsuche(e.target.value)}
                  placeholder="Bezeichnung oder Artikelnummer"
                  autoComplete="off"
                />
              ) : (
                <p className="kanalliste__wert">
                  {artikel.artikelnummer} · {artikel.bezeichnung}{' '}
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    onClick={() => setzeArtikel(undefined)}
                  >
                    Anderen wählen
                  </button>
                </p>
              )}
              {artikel === undefined && artikeltreffer.length > 0 && (
                <ul className="kanalliste" aria-label="Treffer der Artikelsuche">
                  {artikeltreffer.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        onClick={() => {
                          setzeArtikel(a);
                          setzeArtikeltreffer([]);
                        }}
                      >
                        {a.artikelnummer} · {a.bezeichnung}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="feld__hilfe">
                Die Preisbindung gilt für genau diesen Artikel und nur für Abrufe aus diesem Rahmen
                — sie ist keine Preisliste für den Kunden.
              </p>
            </div>
          </Feldraster>
        </Abschnitt>

        <Abschnitt titel="Kontingent und Preis">
          <Feldraster>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ra-menge">
                Abrufmenge
              </label>
              <input
                id="ra-menge"
                type="text"
                value={abrufmenge}
                onChange={(e) => setzeAbrufmenge(e.target.value)}
              />
            </div>

            {/* PRF-05 */}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ra-preis">
                Gebundener Preis
              </label>
              <input
                id="ra-preis"
                type="text"
                value={preis}
                onChange={(e) => setzePreis(e.target.value)}
                placeholder="leer lassen für den Listenpreis"
              />
              <p className="feld__hilfe">
                Bleibt das Feld leer, gilt der Preis, der zum <strong>Beginn der Laufzeit</strong>
                {' '}in der Preisliste steht — nicht der von heute.
              </p>
            </div>
          </Feldraster>
        </Abschnitt>

        <Abschnitt titel="Laufzeit">
          <Feldraster>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ra-von">
                Laufzeit von
              </label>
              <input id="ra-von" type="date" value={von} onChange={(e) => setzeVon(e.target.value)} />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ra-bis">
                Laufzeit bis
              </label>
              <input id="ra-bis" type="date" value={bis} onChange={(e) => setzeBis(e.target.value)} />
            </div>

            {/* AUF-27 */}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ra-regel">
                Nicht abgerufene Menge bei Laufzeitende
              </label>
              <select
                id="ra-regel"
                value={regel}
                onChange={(e) => setzeRegel(e.target.value as Restmengenregel | '')}
              >
                <option value="">noch offen — wird bei Laufzeitende gefragt</option>
                {(Object.keys(RESTMENGENNAME) as Restmengenregel[]).map((r) => (
                  <option key={r} value={r}>
                    {RESTMENGENNAME[r]}
                  </option>
                ))}
              </select>
              <p className="feld__hilfe">
                „Verfällt" wird bei Laufzeitende angewandt; „verlängern" und „wird berechnet"
                werden vorgeschlagen — beides sind Willenserklärungen gegenüber dem Kunden und
                trägt ein Mensch, kein nächtlicher Lauf.
              </p>
            </div>

            {regel === 'verlaengern' && (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="ra-monate">
                  Verlängerung um … Monate
                </label>
                <input
                  id="ra-monate"
                  type="number"
                  min="1"
                  max="60"
                  value={monate}
                  onChange={(e) => setzeMonate(e.target.value)}
                />
              </div>
            )}
          </Feldraster>
        </Abschnitt>
      </Erfassung>
    </div>
  );
}

type Rahmenreiter = 'abruf' | 'vereinbarung';

/**
 * Die Rahmenauftragsakte (AUF-25 bis AUF-27).
 *
 * DER ABRUF STEHT OBEN. Er ist die einzige Handlung, die täglich vorkommt;
 * alles andere — Laufzeit, Schwellen, Restmengenregel — wird einmal gepflegt.
 * Deshalb ist „Abruf" der erste Reiter und „Vereinbarung" der zweite.
 *
 * Aufbau nach der Kundenakte (ÄA-41): Kopf mit Sinnbild, Status und
 * Kennzahlenleiste (Ausschöpfung, Restmenge, Preis, Laufzeit); links die
 * Reiter, rechts das Zeitliche — der Verbrauch und die Abrufe in der Folge,
 * in der sie entstanden sind.
 */
export function Rahmenauftragsakte({
  zurKunde,
  id,
  darfAendern,
  darfAbrufen,
  beiKopf,
  beiListe,
  beiAuftrag,
}: {
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  readonly id: string;
  readonly darfAendern: boolean;
  readonly darfAbrufen: boolean;
  readonly beiKopf: (kopf: Kopf) => void;
  readonly beiListe: () => void;
  readonly beiAuftrag: (id: string) => void;
}): JSX.Element {
  const [rahmen, setzeRahmen] = useState<Rahmenauftrag | undefined>(undefined);
  const [reiter, setzeReiter] = useState<Rahmenreiter>('abruf');
  const [menge, setzeMenge] = useState('1');
  const [referenz, setzeReferenz] = useState('');
  /* L5 (CRM-116): der Termin der Abrufzeile und die Frist der Option (BUC-76). */
  const [abruftermin, setzeAbruftermin] = useState('');
  const [abruffrist, setzeAbruffrist] = useState('');
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [restfrage, setzeRestfrage] = useState(false);

  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeRahmen(await holeAkte(id));
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  /*
   * DIE RÜCKRUFE DES KOPFES STEHEN IN EINEM REF UND NICHT IN DEN
   * ABHÄNGIGKEITEN — wie in der Kundenakte. Die Arbeitsfläche reicht sie als
   * Pfeilfunktionen herein, also bei jedem Bild neu; stünden sie in der Liste,
   * setzte jeder Kopf ein neues Bild und jedes Bild einen neuen Kopf.
   */
  const rueckrufe = useRef({ beiListe });
  rueckrufe.current = { beiListe };

  useEffect(() => {
    if (rahmen === undefined) return;
    const geladen = rahmen;
    const stand = geladen.kennzahlen;
    const zahlen: Kennzahl[] = [
      /* AUF-26: die Restmenge ist jederzeit sichtbar. */
      { beschriftung: 'Ausschöpfung', wert: `${alsProzent(stand.ausgeschoepftProzent)} abgerufen` },
      { beschriftung: 'Restmenge', wert: `${alsMenge(stand.restmenge)} von ${alsMenge(stand.abrufmenge)}` },
      { beschriftung: 'Gebundener Preis', wert: alsEuro(geladen.gebundenerPreis) },
      {
        beschriftung: 'Laufzeit bis',
        wert: alsTag(geladen.laufzeitBis) ?? '—',
        /* Innerhalb des Meldevorlaufs ist das Laufzeitende eine Frist (AUF-26). */
        frist: stand.tageBisAblauf >= 0 && stand.tageBisAblauf <= geladen.meldevorlaufTage,
      },
    ];
    beiKopf({
      kicker: 'Rahmenauftrag',
      titel: `${geladen.rahmennummer} · ${geladen.bezeichnung}`,
      // Die kurze Zeile unter dem Titel: für wen und wie lange.
      unterzeile: `${geladen.kundenname} · ${alsTag(geladen.laufzeitVon) ?? '—'} bis ${alsTag(geladen.laufzeitBis) ?? '—'}`,
      sinnbild: 'clipboard-text',
      kennzeichen: (
        <span className={markenklasse(STATUSMARKE[geladen.status])}>
          {RAHMENSTATUSNAME[geladen.status]}
        </span>
      ),
      kennzahlen: <Kennzahlenleiste zahlen={zahlen} />,
      handlungen: (
        <button type="button" className="knopf" onClick={() => rueckrufe.current.beiListe()}>
          Zur Liste
        </button>
      ),
    });
  }, [rahmen, beiKopf]);

  async function handle(was: () => Promise<unknown>, meldung: string): Promise<void> {
    try {
      await wirke(async () => {
        await was();
        return meldung;
      });
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  /** Der Kern von `handle` ohne den Fang — hinter einer Rückfrage bleibt der Fehler im Pop-up (CRM-103). */
  async function wirke(was: () => Promise<string>): Promise<void> {
    const meldung = await was();
    setzeRueckmeldung(meldung);
    setzeFehler(undefined);
    await laden();
  }

  /* AUF-27: Die Antwort des Servers wird zur Rückmeldung. */
  async function restAbschliessen(id: string): Promise<string> {
    const ergebnis = await restmengeAbschliessen(id);
    return ergebnis.angewandt
      ? `Restmenge ${ergebnis.restmenge} verfallen; der Rahmen ist abgeschlossen.`
      : ergebnis.vorschlag === null
        ? `Es sind ${ergebnis.restmenge} offen, und es ist keine Regel vereinbart. Bitte entscheiden.`
        : `Vorschlag für ${ergebnis.restmenge} offene: ${RESTMENGENNAME[ergebnis.vorschlag]}. Die Entscheidung trägt ein Mensch.`;
  }

  if (fehler !== undefined && rahmen === undefined) {
    return (
      <>
        <Meldung fehler={fehler} />
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={beiListe}>
            Zurück zur Liste
          </button>
        </div>
      </>
    );
  }

  if (rahmen === undefined) {
    return <p role="status">Wird geladen …</p>;
  }

  const rest = Number(rahmen.kennzahlen.restmenge);
  const verfaellt = rahmen.restmengenregel === 'verfallen';

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      <Aktenrumpf
        haupt={
          <>
            <Reiterleiste
              beschriftung="Bereiche des Rahmenauftrags"
              praefix="rahmen-"
              gewaehlt={reiter}
              beiWahl={setzeReiter}
              reiter={[
                { kennung: 'abruf', beschriftung: 'Abruf' },
                { kennung: 'vereinbarung', beschriftung: 'Vereinbarung' },
              ]}
            />

            <div
              role="tabpanel"
              id={`tafel-rahmen-${reiter}`}
              aria-labelledby={`reiter-rahmen-${reiter}`}
            >
              {reiter === 'abruf' && (
                <div className="tafel__abschnitte">
                  {/* AUF-25 */}
                  <Abschnitt titel="Neuer Abruf">
                    {darfAbrufen && rahmen.status === 'aktiv' && rest > 0 && (
                      <>
                        <Feldraster>
                          <div className="feld">
                            <label className="feld__beschriftung" htmlFor="ra-abruf-menge">
                              Menge
                            </label>
                            <input
                              id="ra-abruf-menge"
                              type="number"
                              min="1"
                              max={String(Math.max(1, Math.floor(rest)))}
                              value={menge}
                              onChange={(e) => setzeMenge(e.target.value)}
                            />
                          </div>
                          <div className="feld">
                            <label className="feld__beschriftung" htmlFor="ra-abruf-referenz">
                              Bestellreferenz
                            </label>
                            <input
                              id="ra-abruf-referenz"
                              type="text"
                              value={referenz}
                              onChange={(e) => setzeReferenz(e.target.value)}
                            />
                          </div>
                        </Feldraster>
                        {/*
                          L5 (CRM-116): Beim Einzelplatz wird der Termin wie im
                          Angebot an der Zeile gewählt. Gebucht wird mit der
                          Bestätigung des Abrufs (BUC-75); mit Frist hält der
                          Abruf die Plätze schon jetzt als Option (BUC-76).
                        */}
                        {rahmen.vertriebsform === 'einzelplatz' && (
                          <>
                            <Platzterminwahl
                              id="ra-abruf-termin"
                              artikel={rahmen.artikel}
                              kunde={rahmen.kunde}
                              menge={Number(menge === '' ? '0' : menge)}
                              wert={abruftermin}
                              beiWahl={(t) => {
                                setzeAbruftermin(t);
                                if (t === '') setzeAbruffrist('');
                              }}
                            />
                            {abruftermin !== '' && (
                              <div className="feld">
                                <label className="feld__beschriftung" htmlFor="ra-abruf-frist">
                                  Plätze zurücklegen bis
                                </label>
                                <input
                                  id="ra-abruf-frist"
                                  type="date"
                                  value={abruffrist}
                                  onChange={(e) => setzeAbruffrist(e.target.value)}
                                />
                                <p className="feld__hilfe">
                                  {abruffrist === ''
                                    ? 'Leer heißt: Es wird nichts zurückgelegt. Die Plätze kommen '
                                      + 'mit der Bestätigung des Abrufs.'
                                    : 'Die Plätze sind bis zum Ende dieses Tages für den Kunden '
                                      + 'gehalten; danach verfallen sie von selbst.'}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                        <div className="knopfreihe">
                          <button
                            type="button"
                            className="knopf knopf--haupt"
                            disabled={menge === '' || Number(menge) < 1 || Number(menge) > rest}
                            onClick={() =>
                              void handle(async () => {
                                const neu = await abrufen(rahmen.id, {
                                  menge: Number(menge),
                                  belegdatum: heute(),
                                  ...(referenz === '' ? {} : { bestellreferenz: referenz }),
                                  ...(abruftermin === '' ? {} : { termin: abruftermin }),
                                  ...(abruftermin !== '' && abruffrist !== ''
                                    ? { optionsfrist: abruffrist }
                                    : {}),
                                });
                                beiAuftrag(neu.id);
                              }, 'Abruf angelegt.')
                            }
                          >
                            Abrufen
                          </button>
                        </div>
                        <p className="feld__hilfe">
                          Der Abruf legt einen gewöhnlichen Auftrag an — mit dem gebundenen Preis
                          statt dem der Preisliste. Alles Weitere gilt für ihn wie für jeden
                          Auftrag.
                        </p>
                      </>
                    )}

                    {rest <= 0 && (
                      <p className="warnung">
                        Der Rahmen ist ausgeschöpft. Für mehr erhöhen Sie die Abrufmenge — das ist
                        eine Vertragsänderung — oder legen einen gewöhnlichen Auftrag ohne
                        Preisbindung an.
                      </p>
                    )}

                    {!darfAbrufen && rahmen.status === 'aktiv' && rest > 0 && (
                      <p className="feld__hilfe">
                        Abrufen darf, wer Aufträge anlegen darf — der Abruf legt einen Auftrag an.
                      </p>
                    )}

                    {rest > 0 && rahmen.status !== 'aktiv' && (
                      <p className="feld__hilfe">
                        Abgerufen wird nur aus einem aktiven Rahmen; dieser steht auf „
                        {RAHMENSTATUSNAME[rahmen.status]}".
                      </p>
                    )}
                  </Abschnitt>

                  <Abschnitt titel="Konditionen des Abrufs">
                    <Feldraster>
                      <Feldzeile
                        beschriftung="Gebundener Preis"
                        wert={rahmen.gebundenerPreis}
                        anzeige={`${alsEuro(rahmen.gebundenerPreis)} · ${rahmen.steuerschluessel} (${alsProzent(rahmen.steuerProzent)})`}
                      />
                      <Feldzeile
                        beschriftung="Artikel"
                        wert={`${rahmen.artikelnummer ?? '—'} · ${rahmen.artikelbezeichnung ?? '—'}`}
                      />
                      <Feldzeile
                        beschriftung="Laufzeit"
                        wert={`${alsTag(rahmen.laufzeitVon) ?? '—'} bis ${alsTag(rahmen.laufzeitBis) ?? '—'}${
                          rahmen.kennzahlen.tageBisAblauf >= 0
                            ? ` · noch ${rahmen.kennzahlen.tageBisAblauf} Tage`
                            : ''
                        }`}
                      />
                      <Feldzeile
                        beschriftung="Kunde"
                        wert={rahmen.kundenname}
                        anzeige={
                          <KundeVerweis kundeId={rahmen.kunde} zurAkte={zurKunde}>
                            {rahmen.kundenname}
                          </KundeVerweis>
                        }
                      />
                    </Feldraster>
                  </Abschnitt>
                </div>
              )}

              {/* AUF-26, AUF-27 */}
              {reiter === 'vereinbarung' && (
                <div className="tafel__abschnitte">
                  <Abschnitt titel="Meldungen und Laufzeitende">
                    <Feldraster>
                      <Feldzeile
                        beschriftung="Meldeschwelle"
                        kennung="ra-schwelle"
                        wert={rahmen.meldeschwelleProzent}
                        anzeige={`${alsProzent(rahmen.meldeschwelleProzent)}${
                          rahmen.schwelleGemeldetAm === null ? '' : ' · gemeldet'
                        }`}
                        hilfe="In Prozent. Wird sie oder die Abrufmenge geändert, gilt die Ausschöpfung wieder als ungemeldet — sonst schwiege das System beim nächsten Erreichen."
                        {...(darfAendern
                          ? {
                              beiSichern: (w: string | null) =>
                                handle(
                                  () =>
                                    feldSichern(rahmen.id, 'meldeschwelleProzent', w, rahmen.stand),
                                  'Meldeschwelle geändert; die Meldung wird erneut fällig.',
                                ),
                            }
                          : {})}
                      />
                      <Feldzeile
                        beschriftung="Vorlauf zum Ablauf"
                        wert={`${rahmen.meldevorlaufTage} Tage${
                          rahmen.ablaufGemeldetAm === null ? '' : ' · gemeldet'
                        }`}
                      />
                      <Feldzeile
                        beschriftung="Restmenge bei Laufzeitende"
                        wert={`${
                          rahmen.restmengenregel === null
                            ? 'noch offen'
                            : RESTMENGENNAME[rahmen.restmengenregel]
                        }${
                          rahmen.verlaengerungMonate === null
                            ? ''
                            : ` · um ${rahmen.verlaengerungMonate} Monate`
                        }`}
                      />
                      <Feldzeile beschriftung="Abrufmenge" wert={alsMenge(rahmen.abrufmenge)} />
                    </Feldraster>

                    {darfAendern && (
                      <div className="knopfreihe">
                        {/*
                          CRM-103: Nur „verfallen" wirkt sofort und schließt den Rahmen;
                          jede andere Regel bringt bloß einen Vorschlag und fragt nicht nach.
                        */}
                        <button
                          type="button"
                          className="knopf"
                          onClick={() =>
                            verfaellt
                              ? setzeRestfrage(true)
                              : void wirke(() => restAbschliessen(rahmen.id)).catch((ursache: unknown) =>
                                  setzeFehler(ursache as ApiFehler),
                                )
                          }
                        >
                          {verfaellt ? 'Restmenge abschließen …' : 'Restmenge abschließen'}
                        </button>
                      </div>
                    )}
                    {restfrage && (
                      <Rueckfrage
                        frage="Restmenge abschließen?"
                        unterzeile={`${rahmen.rahmennummer} · ${rahmen.bezeichnung}`}
                        tat="Abschließen"
                        beiSchliessen={() => setzeRestfrage(false)}
                        beiBestaetigen={async () => {
                          await wirke(() => restAbschliessen(rahmen.id));
                          setzeRestfrage(false);
                        }}
                      >
                        {/* AUF-27: vereinbart ist „verfallen". */}
                        <p>
                          {alsMenge(rahmen.kennzahlen.restmenge)} von {alsMenge(rahmen.abrufmenge)}{' '}
                          verfallen, und der Rahmen wird abgeschlossen. Abgerufen wird danach nicht
                          mehr — das geht nur aus einem aktiven Rahmen.
                        </p>
                      </Rueckfrage>
                    )}
                  </Abschnitt>

                  <Abschnitt titel="Angelegt">
                    <Feldraster>
                      <Feldzeile beschriftung="Rahmennummer" wert={rahmen.rahmennummer} />
                      <Feldzeile beschriftung="Bearbeitung" wert={rahmen.bearbeiterName} />
                      <Feldzeile beschriftung="Angelegt am" wert={alsTag(rahmen.angelegtAm)} />
                      {rahmen.bemerkung !== null && (
                        <Feldzeile beschriftung="Bemerkung" wert={rahmen.bemerkung} />
                      )}
                    </Feldraster>
                  </Abschnitt>
                </div>
              )}
            </div>
          </>
        }
        seite={
          <>
            <div className="akte__seitentitel">
              <span>Verbrauch</span>
            </div>
            {/* AUF-26: als Text und als Balken (WCAG 1.4.1). */}
            <p>
              <Ausschoepfung stand={rahmen.kennzahlen} />
            </p>
            <p className="feld__hilfe">
              Restmenge <strong>{alsMenge(rahmen.kennzahlen.restmenge)}</strong> aus{' '}
              {rahmen.kennzahlen.abrufe} {rahmen.kennzahlen.abrufe === 1 ? 'Abruf' : 'Abrufen'}.
            </p>
            <Verlauf
              titel="Abrufe"
              zusatz={String(rahmen.abrufe.length)}
              eintraege={rahmen.abrufe.map(
                (a): Verlaufseintrag => ({
                  kennung: a.id,
                  art: a.status === 'storniert' ? 'still' : 'vorgang',
                  sinnbild: 'file-text',
                  titel: a.version > 1 ? `${a.auftragsnummer} · V${a.version}` : a.auftragsnummer,
                  detail: `${alsMenge(a.menge)} · ${AUFTRAGSSTATUSNAME[a.status]}`,
                  datum: alsTag(a.belegdatum) ?? '—',
                  beiWahl: () => beiAuftrag(a.id),
                }),
              )}
              leer="Noch kein Abruf."
            />
          </>
        }
      />
    </>
  );
}
